/**
 * Floating table headers, ported from the prototype's fhSchedule/fhBuild/fhUpdate: when a table's header scrolls
 * under the sticky filter bar (or the top of an open drawer), a clone of the header is pinned there and kept in
 * step with sideways scrolling. Works on any `table.data` / `table.wall` carrying a `data-sid`.
 * Clones are made with cloneNode; clicking a cloned header clicks the real one, so React sorting still applies.
 */

interface Clone {
  box: HTMLDivElement;
  thead: HTMLTableSectionElement;
  ver: string | undefined;
  w: number;
}
const FH = { map: new Map<HTMLTableElement, Clone>(), raf: 0 };

export function fhSchedule(): void {
  if (!FH.raf) FH.raf = requestAnimationFrame(fhUpdate);
}

function fhBuild(t: HTMLTableElement, inDrawer: boolean): Clone {
  const box = document.createElement('div');
  box.className = 'fhead' + (inDrawer ? ' indrawer' : '');
  const ct = t.cloneNode(false) as HTMLTableElement;
  ct.removeAttribute('id');
  ct.dataset.for = t.dataset.sid;
  ct.style.width = t.getBoundingClientRect().width + 'px';
  const h = t.tHead!.cloneNode(true) as HTMLTableSectionElement;
  ct.appendChild(h);
  const src = [...t.tHead!.querySelectorAll('th')];
  const dst = [...h.querySelectorAll('th')];
  src.forEach((s, i) => {
    const r = s.getBoundingClientRect();
    // The copy sits outside the drawer, so drawer-only rules do not reach it: carry over what sets the label width.
    const cs = getComputedStyle(s);
    Object.assign(dst[i]!.style, {
      paddingLeft: cs.paddingLeft,
      paddingRight: cs.paddingRight,
      fontSize: cs.fontSize,
      letterSpacing: cs.letterSpacing,
      whiteSpace: cs.whiteSpace,
      width: r.width + 'px',
      minWidth: r.width + 'px',
      maxWidth: r.width + 'px',
      height: r.height + 'px',
      boxSizing: 'border-box',
    });
  });
  h.querySelectorAll<HTMLElement>('[tabindex],button').forEach((x) => (x.tabIndex = -1));
  box.appendChild(ct);
  document.body.appendChild(box);
  return { box, thead: t.tHead!, ver: t.tHead!.dataset.ver, w: t.getBoundingClientRect().width };
}

function fhUpdate(): void {
  FH.raf = 0;
  const dr = document.getElementById('drawer');
  const dOpen = !!dr && dr.classList.contains('open');
  const fb = document.querySelector<HTMLElement>('.filters');
  // Under the filters while they show; under the pinned filter bar once they have scrolled away.
  const bar = document.querySelector<HTMLElement>('.fbar.on');
  // Inside the site's shell nothing pins above the main panel's top edge (the top bar sits there).
  const main = document.querySelector<HTMLElement>('.appmain');
  const floor = main ? main.getBoundingClientRect().top : 0;
  const pageTop = Math.max(
    fb && fb.offsetParent ? Math.max(floor, fb.getBoundingClientRect().bottom) : floor,
    bar ? bar.getBoundingClientRect().bottom : 0,
  );
  const dTop = dOpen ? document.getElementById('dScroll')!.getBoundingClientRect().top : 0;
  const seen = new Set<HTMLTableElement>();
  const moves: Array<{ c: Clone; top: string; left: string; width: string; x: number }> = [];
  document.querySelectorAll<HTMLTableElement>('table.data,table.wall').forEach((t) => {
    // A table shown as cards on a phone hides its header row; there is nothing to float.
    if (t.closest('.fhead') || !t.tHead || !t.offsetParent || !t.tHead.getClientRects().length) return;
    // Plain tables (admin pages) get an id on first sight, so their headers float too.
    if (!t.dataset.sid) t.dataset.sid = nextSid();
    // The admin drawer scrolls on its own; its tables keep their headers in place.
    if (t.closest('.adrawer')) return;
    // Sections that scroll inside themselves keep their own sticky header; a page-level clone would double it.
    if (t.closest('#pWall .wallwrap, .scrollpanel .tablewrap')) return;
    const inDrawer = !!t.closest('#drawer');
    if (dOpen !== inDrawer) return;
    const top = inDrawer ? dTop : pageTop;
    const hr = t.tHead.getBoundingClientRect();
    const tr = t.getBoundingClientRect();
    if (!(hr.top < top - 1 && tr.bottom > top + hr.height + 10)) return;
    let c = FH.map.get(t);
    const w = tr.width;
    if (!c || c.thead !== t.tHead || c.ver !== t.tHead.dataset.ver || Math.abs(c.w - w) > 0.5) {
      if (c) c.box.remove();
      c = fhBuild(t, inDrawer);
      FH.map.set(t, c);
    }
    const wrap = t.parentElement!;
    const wr = wrap.getBoundingClientRect();
    moves.push({ c, top: top + 'px', left: wr.left + 'px', width: wrap.clientWidth + 'px', x: wrap.scrollLeft });
    seen.add(t);
  });
  // Every position is measured first and written after, so a scroll frame lays the page out once, not once per
  // table; unchanged values are not written at all.
  for (const m of moves) {
    const st = m.c.box.style;
    if (st.display !== 'block') st.display = 'block';
    if (st.top !== m.top) st.top = m.top;
    if (st.left !== m.left) st.left = m.left;
    if (st.width !== m.width) st.width = m.width;
    if (m.c.box.scrollLeft !== m.x) m.c.box.scrollLeft = m.x;
  }
  for (const [t, c] of FH.map) {
    if (!seen.has(t)) {
      if (c.box.style.display !== 'none') c.box.style.display = 'none';
      if (!document.contains(t)) {
        c.box.remove();
        FH.map.delete(t);
      }
    }
  }
}

let sid = 0;
/** A unique data-sid for a table. */
export const nextSid = () => 't' + ++sid;

/** Installs the scroll/resize listeners and the clone click relay. Returns an uninstall function. */
export function installFloatingHeaders(): () => void {
  const onScroll = () => fhSchedule();
  const onClick = (e: MouseEvent) => {
    const th = (e.target as HTMLElement).closest<HTMLElement>('.fhead th[data-col]');
    if (!th || (e.target as HTMLElement).closest('button,a')) return;
    const clone = th.closest<HTMLTableElement>('table');
    const real = clone && document.querySelector<HTMLTableElement>(`table[data-sid="${clone.dataset.for}"]`);
    const target = real?.tHead?.querySelector<HTMLElement>(`th[data-col="${th.dataset.col}"]`);
    if (target) {
      e.stopPropagation();
      target.click();
    }
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onScroll);
  document.addEventListener('click', onClick, true);
  return () => {
    document.removeEventListener('scroll', onScroll, { capture: true });
    window.removeEventListener('resize', onScroll);
    document.removeEventListener('click', onClick, true);
    for (const c of FH.map.values()) c.box.remove();
    FH.map.clear();
  };
}
